import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";

function DayToggle({ days, onChangeDays }) {
  const opts = [1, 7, 30];
  return (
    <div className="flex items-center gap-1 rounded-full border border-[color:var(--gold)]/20 p-1">
      {opts.map((d) => (
        <button
          key={d}
          data-testid={`history-range-${d}`}
          onClick={() => onChangeDays(d)}
          className={`px-4 py-1 rounded-full label-overline ${
            days === d
              ? "bg-[color:var(--gold)]/15 text-[color:var(--gold)]"
              : "text-[color:var(--text-secondary)] hover:text-[color:var(--parchment)]"
          }`}
          style={{ transition: "background-color 200ms ease, color 200ms ease" }}
        >
          {d}D
        </button>
      ))}
    </div>
  );
}

export default function HistoryChart({ schumannHistory, cosmicHistory, days, onChangeDays }) {
  const kpByHour = new Map();
  (cosmicHistory?.points || []).forEach((p) => {
    const key = new Date(p.t).toISOString().slice(0, 13);
    kpByHour.set(key, p.kp);
  });

  const merged = (schumannHistory?.points || []).map((p) => {
    const key = new Date(p.t).toISOString().slice(0, 13);
    return {
      t: new Date(p.t).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit" }),
      amp: p.amp,
      kp: kpByHour.get(key) ?? null,
    };
  });

  return (
    <div className="parchment-card rounded-2xl p-8 md:p-10 h-full" data-testid="history-chart">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="label-overline constellation-underline">Historical Field</div>
          <h2 className="mt-3 font-serif-display text-white text-2xl md:text-3xl tracking-tight">
            Amplitude &amp; Kp, together
          </h2>
        </div>
        <DayToggle days={days} onChangeDays={onChangeDays} />
      </div>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={merged} margin={{ top: 8, right: 12, left: 0, bottom: 12 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(212, 162, 76, 0.08)" />
            <XAxis
              dataKey="t"
              stroke="rgba(184, 175, 161, 0.4)"
              tick={{ fontFamily: "Instrument Sans", fontSize: 10, fill: "#7a7565" }}
              minTickGap={40}
            />
            <YAxis
              yAxisId="left"
              stroke="rgba(212, 162, 76, 0.5)"
              tick={{ fontFamily: "Instrument Sans", fontSize: 10, fill: "#7a7565" }}
              width={36}
            />
            <YAxis
              yAxisId="right"
              orientation="right"
              stroke="rgba(229, 168, 124, 0.6)"
              domain={[0, 9]}
              tick={{ fontFamily: "Instrument Sans", fontSize: 10, fill: "#7a7565" }}
              width={30}
            />
            <Tooltip
              contentStyle={{
                background: "rgba(19, 27, 54, 0.96)",
                border: "1px solid rgba(212, 162, 76, 0.3)",
                borderRadius: 6,
                fontFamily: "Instrument Sans",
                fontSize: 12,
              }}
              labelStyle={{ color: "#b8afa1" }}
            />
            <Legend
              wrapperStyle={{ fontFamily: "Instrument Sans", fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", color: "#b8afa1" }}
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="amp"
              name="Schumann"
              stroke="#d4a24c"
              strokeWidth={1.8}
              dot={false}
              isAnimationActive={false}
            />
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="kp"
              name="Kp"
              stroke="#e5a87c"
              strokeWidth={1.8}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
